import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Building, Trash2, Clock, Check, Sparkles, UserIcon, ChevronDown, ChevronUp, Copy, Mail, SprayCan } from 'lucide-react';
import { Building2 } from 'lucide-react';
import {
  createCleaningCompany,
  deleteCleaningCompany,
  getCleaningCompanyProperties,
  delegateCleaningCompanyProperties,
  getProperties,
} from '../lib/api';
import { PropertyPickerModal } from './PropertyPickerModal';
import { AssignedPropertiesDropdown, CleanersDropdown } from './AssignedPropertiesDropdown';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogTitle,
} from './ui/alert-dialog';

export default function CleaningCompaniesSection({ data, onChanged, showAddForm, setShowAddForm }) {
  const companies = data?.companies || [];
  const canAdd = data?.canAdd;

  const [name, setName] = useState('');
  const [managerName, setManagerName] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [delegateModal, setDelegateModal] = useState(null); // {cid, name, properties, selectedIds}
  const [savingDelegation, setSavingDelegation] = useState(false);
  const [allProperties, setAllProperties] = useState([]);
  const [assignNow, setAssignNow] = useState(false);
  const [selectedProps, setSelectedProps] = useState(new Set());
  const [showPicker, setShowPicker] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    getProperties().then((res) => setAllProperties(res.data.properties || [])).catch(() => {});
  }, []);

  const toggleSelectedProp = (pid) => {
    const s = new Set(selectedProps);
    s.has(pid) ? s.delete(pid) : s.add(pid);
    setSelectedProps(s);
  };

  const copyCardLink = (id, link) => {
    navigator.clipboard.writeText(link);
    setCopiedId(id);
    toast.success('Invite link copied!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const propertyIds = assignNow ? Array.from(selectedProps) : [];
      await createCleaningCompany({ name, managerName, managerEmail, mobile: mobile.trim() || null, propertyIds });
      toast.success('Cleaning company invited! An email was sent to the manager.');
      setName(''); setManagerName(''); setManagerEmail(''); setMobile('');
      setAssignNow(false); setSelectedProps(new Set());
      setShowAddForm(false);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite cleaning company');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteCleaningCompany(deleteId);
      toast.success('Cleaning company removed');
      setDeleteId(null);
      onChanged();
    } catch (err) {
      toast.error('Failed to remove cleaning company');
    }
  };

  const openDelegate = async (company) => {
    try {
      const res = await getCleaningCompanyProperties(company.id);
      setDelegateModal({
        cid: company.id,
        name: company.name,
        properties: res.data.allProperties,
        selectedIds: new Set(res.data.delegatedPropertyIds),
      });
    } catch (err) {
      toast.error('Failed to load properties');
    }
  };

  const toggleDelegate = (pid) => {
    if (!delegateModal) return;
    const s = new Set(delegateModal.selectedIds);
    s.has(pid) ? s.delete(pid) : s.add(pid);
    setDelegateModal({ ...delegateModal, selectedIds: s });
  };

  const saveDelegation = async () => {
    if (!delegateModal) return;
    setSavingDelegation(true);
    try {
      await delegateCleaningCompanyProperties(delegateModal.cid, Array.from(delegateModal.selectedIds));
      toast.success('Delegated properties updated');
      setDelegateModal(null);
      onChanged();
    } catch (err) {
      toast.error('Failed to update delegated properties');
    } finally {
      setSavingDelegation(false);
    }
  };

  return (
    <div className="mt-10" data-testid="cleaning-companies-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Building size={24} className="text-purple-500" />
          Cleaning Companies ({companies.length})
        </h2>
      </div>

      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddForm(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="cleaning-company-modal"
          >
            <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Sparkles size={26} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white m-0">Invite a Cleaning Company</h3>
              <p className="text-white/80 text-sm mt-1">They'll get a manager account to add their own cleaners and manage the properties you delegate.</p>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4" data-testid="cleaning-company-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Cleaning company name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="SparkleClean Ltd" className="input-3d w-full" data-testid="cc-name-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Manager name</label>
                <input value={managerName} onChange={(e) => setManagerName(e.target.value)} required placeholder="Marco Rossi" className="input-3d w-full" data-testid="cc-manager-name-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Manager email</label>
                <input type="email" value={managerEmail} onChange={(e) => setManagerEmail(e.target.value)} required placeholder="manager@sparkleclean.com" className="input-3d w-full" data-testid="cc-manager-email-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Mobile <span className="text-slate-400 font-normal">(optional)</span></label>
                <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+44 7123 456789" className="input-3d w-full" data-testid="cc-mobile-input" />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="cc-assign-section">
                <label className="flex items-center justify-between cursor-pointer">
                  <span>
                    <span className="block text-sm font-semibold text-slate-700">Delegate properties now</span>
                    <span className="block text-xs text-slate-400 mt-0.5">Optional — you can also delegate them later with "Manage Properties".</span>
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={assignNow}
                    onClick={() => setAssignNow((v) => !v)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${assignNow ? 'bg-purple-500' : 'bg-slate-300'}`}
                    data-testid="cc-assign-toggle"
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${assignNow ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </label>
                {assignNow && (
                  <button
                    type="button"
                    onClick={() => setShowPicker(true)}
                    className="mt-3 w-full h-11 rounded-xl bg-white border border-purple-200 text-purple-600 text-sm font-semibold hover:bg-purple-50 transition-colors"
                    data-testid="cc-choose-properties"
                  >
                    {selectedProps.size > 0 ? `${selectedProps.size} propert${selectedProps.size === 1 ? 'y' : 'ies'} selected` : 'Choose properties'}
                  </button>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowAddForm(false)} className="btn-3d btn-3d-outline h-12 px-6 rounded-2xl">Cancel</button>
                <button type="submit" disabled={creating} className="btn-3d flex-1 h-12 rounded-2xl font-semibold bg-gradient-to-r from-purple-500 to-blue-500 text-white" data-testid="cc-submit-button">
                  {creating ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {companies.length === 0 ? (
        !showAddForm && (
          <div className="card-3d p-8 text-center" data-testid="cleaning-companies-empty">
            <Building size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No cleaning companies yet</h3>
            <p className="text-slate-500 mb-4">Invite an external cleaning team to manage the properties you delegate.</p>
            {canAdd && (
              <button onClick={() => setShowAddForm(true)} className="btn-3d h-11 px-5 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold" data-testid="cc-add-empty-button">
                Add cleaning company
              </button>
            )}
          </div>
        )
      ) : (
        <div className="space-y-4">
          {companies.map((c) => {
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-3d p-5" data-testid={`cleaning-company-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' }}>
                      <SprayCan size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 leading-tight">{c.name}</h3>
                      <p className="text-xs text-slate-500 mt-1">{c.manager?.name} · {c.manager?.email}</p>
                      {c.status !== 'active' && (
                        <div className="mt-2">
                          <span className="badge-3d badge-warning text-xs"><Clock size={12} /> Manager invite pending</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button onClick={() => setDeleteId(c.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-cleaning-company-${c.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="mt-4">
                  <AssignedPropertiesDropdown
                    properties={c.delegatedProperties || []}
                    accent="purple"
                    testid={`cc-properties-dropdown-${c.id}`}
                  />
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3">
                  <button
                    onClick={() => openDelegate(c)}
                    className="h-10 rounded-xl bg-purple-50 text-purple-600 border border-purple-200 text-sm font-semibold hover:bg-purple-100 transition-colors"
                    data-testid={`delegate-properties-${c.id}`}
                  >
                    Manage Properties
                  </button>
                  <CleanersDropdown
                    cleaners={c.cleaners || []}
                    pending={(c.pendingInvites || []).filter((i) => i.inviteType === 'cleaning_company_staff')}
                    testid={`cc-cleaners-dropdown-${c.id}`}
                  />
                </div>

                {c.status !== 'active' && c.managerInviteLink && (
                  <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`cc-invite-link-${c.id}`}>
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
                      <Mail size={13} className="text-purple-400" />
                      Manager invite link — share it if the email didn't arrive
                    </p>
                    <div className="flex gap-2">
                      <input readOnly value={c.managerInviteLink} className="input-3d flex-1 bg-slate-50 text-xs font-mono" />
                      <button
                        onClick={() => copyCardLink(c.id, c.managerInviteLink)}
                        className="btn-3d h-11 px-4 rounded-xl bg-purple-500 text-white"
                        data-testid={`cc-copy-link-${c.id}`}
                      >
                        {copiedId === c.id ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="rounded-3xl border-0 shadow-2xl p-0 overflow-hidden max-w-md bg-white">
          <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Trash2 size={32} className="text-white" />
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-white m-0">Remove Cleaning Company?</AlertDialogTitle>
          </div>
          <div className="p-6 bg-white">
            <AlertDialogDescription className="text-center text-slate-600 text-base m-0">
              This removes the manager and all their cleaners, along with their property assignments. This cannot be undone.
            </AlertDialogDescription>
          </div>
          <AlertDialogFooter className="p-6 pt-0 flex gap-3 bg-white">
            <AlertDialogCancel className="flex-1 h-12 rounded-xl font-semibold border-2 border-slate-200 hover:bg-slate-50 bg-white text-slate-700">Keep</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="flex-1 h-12 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-red-600 hover:opacity-90 border-0 text-white" data-testid="confirm-delete-cleaning-company">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AnimatePresence>
        {delegateModal && (
          <PropertyPickerModal
            title="Manage Properties"
            subtitle={`for ${delegateModal.name}`}
            properties={delegateModal.properties}
            selectedIds={delegateModal.selectedIds}
            onToggle={toggleDelegate}
            onSave={saveDelegation}
            saving={savingDelegation}
            onClose={() => setDelegateModal(null)}
            emptyText="Create properties first, then delegate them here."
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPicker && (
          <PropertyPickerModal
            title="Delegate Properties"
            subtitle="to this cleaning company"
            properties={allProperties}
            selectedIds={selectedProps}
            onToggle={toggleSelectedProp}
            onSave={() => setShowPicker(false)}
            saving={false}
            onClose={() => setShowPicker(false)}
            emptyText="Create properties first, then delegate them here."
          />
        )}
      </AnimatePresence>
    </div>
  );
}
